import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, deleteDoc, doc, where } from 'firebase/firestore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Lock, RefreshCw, Calendar, Trash2, Download } from 'lucide-react';
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
// import { Calendar as CalendarComponent } from "./ui/calendar" // Assuming you might have a calendar component, or use native date inputs for simplicity

const SubmissionsPage = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [submissions, setSubmissions] = useState([]);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Date filter states
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === "R3dhat1!!") {
            setIsAuthenticated(true);
            fetchSubmissions();
        } else {
            setError("Invalid password");
        }
    };

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "enquiries"), orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAtDate: doc.data().createdAt?.toDate() || new Date(),
                createdAtLabel: doc.data().createdAt?.toDate()
                    ? doc.data().createdAt.toDate().toLocaleString()
                    : 'Just now'
            }));
            setSubmissions(data);
            setFilteredSubmissions(data);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load submissions.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this submission?")) {
            try {
                await deleteDoc(doc(db, "enquiries", id));
                // Update local state
                const updated = submissions.filter(item => item.id !== id);
                setSubmissions(updated);
                filterData(updated, startDate, endDate);
            } catch (err) {
                console.error("Error deleting document:", err);
                alert("Failed to delete submission");
            }
        }
    };

    const handleDateFilter = () => {
        filterData(submissions, startDate, endDate);
    };

    const filterData = (data, start, end) => {
        if (!start && !end) {
            setFilteredSubmissions(data);
            return;
        }

        const startD = start ? new Date(start) : new Date('2000-01-01');
        const endD = end ? new Date(end) : new Date();
        // Set end date to end of day
        endD.setHours(23, 59, 59, 999);

        const filtered = data.filter(item => {
            const itemDate = item.createdAtDate;
            return itemDate >= startD && itemDate <= endD;
        });
        setFilteredSubmissions(filtered);
    };

    const downloadCSV = () => {
        const headers = ["Date", "Name", "Mobile", "City", "Investment Readiness"];

        const csvRows = [
            headers.join(','), // header row
            ...filteredSubmissions.map(row => {
                const values = [
                    `"${row.createdAtLabel}"`,
                    `"${row.name}"`,
                    `"${row.mobile}"`,
                    `"${row.city}"`,
                    `"${row.investment}"`
                ];
                return values.join(',');
            })
        ];

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "franchise_enquiries.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        if (isAuthenticated) {
            handleDateFilter();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Protected Access</h2>
                        <p className="text-gray-500 mt-2">Please enter the password to view submissions.</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter password"
                                className="w-full"
                            />
                        </div>
                        {error && <p className="text-red-500 text-sm">{error}</p>}
                        <Button
                            type="submit"
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                        >
                            Access Data
                        </Button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <h1 className="text-3xl font-bold text-gray-900">Franchise Enquiries</h1>
                    <div className="flex gap-2">
                        <Button
                            onClick={downloadCSV}
                            variant="outline"
                            className="flex items-center gap-2 border-green-600 text-green-700 hover:bg-green-50"
                        >
                            <Download className="w-4 h-4" />
                            Export CSV
                        </Button>
                        <Button
                            onClick={fetchSubmissions}
                            variant="outline"
                            className="flex items-center gap-2"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-xl shadow mb-6 flex flex-col md:flex-row gap-4 items-end">
                    <div className="w-full md:w-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full"
                        />
                    </div>
                    <div className="w-full md:w-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full"
                        />
                    </div>
                </div>

                {filteredSubmissions.length === 0 && !loading ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow">
                        <p className="text-gray-500">No submissions found matching your criteria.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investment</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredSubmissions.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.createdAtLabel}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.mobile}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.city}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    {item.investment}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="text-red-600 hover:text-red-900 transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubmissionsPage;
